import ApiClient from '../../client';
import _ from 'lodash';
import { unknownUser } from '@/utils';

const DEFAULT_PAGE_SIZE = 50;

// Latest-request-wins: a slow response for an old search/page must never
// overwrite the result of a newer one (typing fast, or several components
// fetching at once). Module-level because it is not reactive state.
let latestFetchId = 0;

export default {
  namespaced: true,
  state: {
    onlineUserIds: [],
    isLoading: true,
    items: [],
    totalCount: null,
    // True while the last fetched page was full, i.e. another page may exist.
    hasMore: false,
    failed: null
  },
  getters: {
    getById: (state) => (id) => {
      // `items` is an ARRAY of users, so look the user up by matching
      // UserID — not by `items[id]`, which treats the id as an array
      // index and returns undefined for real (string/GUID) UserIDs,
      // surfacing the "Unknown User" placeholder everywhere.
      const list = Array.isArray(state.items) ? state.items : [];
      const user =
        list.find((u) => u && String(u.UserID) === String(id)) ||
        // Legacy fallback in case a caller passes a numeric index / the
        // store is ever shaped as an object keyed by id.
        state.items[id] ||
        unknownUser;

      const clonedUser = _.cloneDeep(user);

      if (clonedUser) {
        clonedUser.Online = !!state.onlineUserIds.find((x) => x === id);
      }

      return clonedUser;
    },
    getUsers: (state) => {
      return state.items;
    },
    getUserTotalCount: (state) => {
      return state.totalCount;
    },
    getHasMore: (state) => {
      return state.hasMore;
    },
    getFailed: (state) => {
      return state.failed;
    },
    isLoading: (state) => {
      return state.isLoading;
    },
    isUserOnline: (state) => (id) => {
      return !!state.onlineUserIds.find((x) => x === id);
    }
  },
  actions: {
    async fetchUsers({ commit }, payload) {
      const fetchId = ++latestFetchId;
      const pageSize = payload.pageSize || DEFAULT_PAGE_SIZE;
      commit('setIsLoading', true);
      try {
        const response = await ApiClient.getUsers(
          payload.page,
          pageSize,
          payload.search,
          payload.groupId
        );
        if (fetchId !== latestFetchId) return;
        const data = Array.isArray(response.data) ? response.data : [];
        commit('setUsers', {
          data,
          append: !!payload.append,
          hasMore: data.length >= pageSize
        });
      } catch (e) {
        if (fetchId !== latestFetchId) return;
        commit('setFailed', { error: e, keepItems: !!payload.append });
      }
    },
    async fetchUserTotalCount({ commit }) {
      const response = await ApiClient.getUserCount();
      commit('setUserTotalCount', response.data);
    },
    updateUser({ state, commit }, payload) {
      if (state.items.indexOf((x) => x.UserID === payload.item?.UserID)) {
        commit('updateUser', payload);
      }
    },
    deleteUser({ commit }, payload) {
      commit('deleteUser', payload);
    }
  },
  mutations: {
    updateOnlineUsers: (state, payload) => {
      state.onlineUserIds = payload?.onlineUserIds || [];
    },
    setIsLoading: (state, payload) => {
      state.isLoading = payload;
    },
    setUsers: (state, payload) => {
      if (payload.append) {
        // Pages can overlap if people were added/removed between requests.
        const seen = new Set(state.items.map((x) => x.UserID));
        state.items = state.items.concat(
          payload.data.filter((x) => !seen.has(x.UserID))
        );
      } else {
        state.items = payload.data;
      }
      state.hasMore = !!payload.hasMore;
      state.isLoading = false;
      state.failed = null;
    },
    setUserTotalCount: (state, payload) => {
      state.totalCount = payload;
    },
    setFailed: (state, { error, keepItems }) => {
      // A failed "load more" must not wipe the rows already on screen.
      if (!keepItems) {
        state.items = [];
        state.hasMore = false;
      }
      state.isLoading = false;
      state.failed = error;
    },
    updateUser: (state, payload) => {
      const itemIndex = state.items.indexOf(
        (x) => x.UserID === payload.item.UserID
      );
      if (itemIndex > -1) {
        state.items[itemIndex] = payload.item;
      }
    },
    deleteUser: (state, payload) => {
      state.items = [...state.items.filter((x) => x.UserID !== payload.item)];
    }
  }
};
